import { useCallback, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

export type AttachmentCheck = { ok: true } | { ok: false; reason: string };

/**
 * What a tool plugs in to take uploads. Each tool keeps its own rules and its own storage, so the same
 * hook serves tools whose files go to different places.
 */
export interface WorkflowAttachmentAdapter<TRef> {
  // The file input's `accept` string.
  accept: string;
  // Whether a file may be staged. The reason is shown to the user when it may not, so it is checked at
  // once rather than failing on Send.
  check: (file: File) => AttachmentCheck;
  // The most files one turn can carry, counting those already staged and those uploaded for a turn the
  // server has not taken yet. Without it there is no cap here (the server still has its own).
  maxFiles?: number;
  // What to tell the user when picked files did not all fit: `added` of them were staged, `skipped` are the
  // names that were not. Without it a plain message is used.
  tooManyFilesMessage?: (details: { limit: number; added: number; skipped: string[] }) => string;
  // The most a turn can carry across all its files, in bytes counted with `bytesOf`, for staged files. A tool
  // sets it when its destination takes only so much in one request. Without it there is no total here.
  maxTotalBytes?: number;
  // What one file adds to that total when it is not simply its size (a photo that gets shrunk adds little).
  bytesOf?: (file: File) => number;
  // What to tell the user when picked files did not all fit the total: `added` were staged, `skipped` are the
  // names that were not, and `totalBytes` is what the whole pick would have come to. Without it a plain
  // message is used.
  tooBigTogetherMessage?: (details: {
    limitBytes: number;
    totalBytes: number;
    added: number;
    skipped: string[];
  }) => string;
  // Uploads one file and returns the reference a turn carries to name it. Rejects with an Error whose
  // message is the server's own reason.
  upload: (
    file: File,
    context: { conversationId: string; groupId: string },
  ) => Promise<TRef>;
  // The name to show for an uploaded file.
  filenameOf: (ref: TRef) => string;
  // A fresh id for the conversation the uploads are filed under. The server has no session yet at the
  // first upload, so the client makes one.
  newConversationId: () => string;
}

export type UploadOutcome<TRef> =
  // Everything is uploaded (or there was nothing to upload): the references a turn should carry,
  // including any kept from an earlier send that failed.
  | { status: 'ready'; attachments: TRef[] }
  // At least one upload failed. The failed files are back in the composer, the ones that uploaded are
  // kept, and the user has been told why.
  | { status: 'failed' }
  // There is no team to file the uploads under; the user has been told.
  | { status: 'no-team' }
  // The conversation was reset while the uploads were in flight: drop this turn.
  | { status: 'cancelled' };

type Notify = (message: string, variant: 'warning' | 'error') => void;

export interface WorkflowAttachmentsController<TRef> {
  accept: string;
  stagedFiles: File[];
  isUploading: boolean;
  stage: (files: File[]) => void;
  remove: (index: number) => void;
  upload: (groupId: string | undefined) => Promise<UploadOutcome<TRef>>;
  // Whether files have uploaded for a turn the server has not taken yet (a send that failed after the
  // upload), so a retry should carry them even if the user stages nothing new.
  hasPendingUploads: () => boolean;
  // Those files themselves, for a composer that shows them next to the staged ones.
  pendingAttachments: TRef[];
  // Drops one of them from the next turn. The stored file stays where it is; nothing refers to it any more.
  removePending: (ref: TRef) => void;
  markSent: () => void;
  reset: () => void;
}

const defaultNotify: Notify = (message, variant) => {
  enqueueSnackbar(message, { variant: variant });
};

/**
 * Staging, uploading and bookkeeping for the files a user attaches to a turn.
 *
 *  - `stage` checks each picked file and refuses at once what the server would refuse at send time. It also
 *    holds a turn to the adapter's `maxFiles`, counting files already staged and files uploaded but not yet
 *    taken: those that do not fit are left out, and the user is told which.
 *  - `upload` sends the staged files in parallel, on Send. Files that uploaded are kept even if a sibling
 *    failed, so a retry does not upload them again; only the failed ones go back to the composer, each
 *    with its own reason.
 *  - `markSent` is called once the server has accepted the turn that carried the files.
 *  - `reset` starts a fresh conversation, and makes an upload still in flight drop its turn.
 */
export const useWorkflowAttachments = <TRef>(
  adapter: WorkflowAttachmentAdapter<TRef>,
  options?: { notify?: Notify },
): WorkflowAttachmentsController<TRef> => {
  // Always the latest, so callers need not memoise the adapter and a stale closure never uploads to an
  // old endpoint.
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;
  const notifyRef = useRef<Notify>(options?.notify || defaultNotify);
  notifyRef.current = options?.notify || defaultNotify;

  const [stagedFiles, setStagedFilesState] = useState<File[]>([]);
  const [isUploading, setIsUploadingState] = useState(false);
  // Mirrors of the state, for the callbacks below to read without going stale.
  const stagedRef = useRef<File[]>([]);
  const uploadingRef = useRef(false);
  // Uploads that succeeded but whose turn has not been accepted yet. The state mirrors the ref so a
  // composer can show them.
  const pendingRef = useRef<TRef[]>([]);
  const [pendingAttachments, setPendingAttachmentsState] = useState<TRef[]>([]);
  const conversationIdRef = useRef<string | null>(null);
  // Bumped by `reset`, so an upload that was in flight does not push its turn into the cleared chat.
  const generationRef = useRef(0);

  const setStaged = useCallback((next: File[]) => {
    stagedRef.current = next;
    setStagedFilesState(next);
  }, []);

  const setPending = useCallback((next: TRef[]) => {
    pendingRef.current = next;
    setPendingAttachmentsState(next);
  }, []);

  const setUploading = useCallback((next: boolean) => {
    uploadingRef.current = next;
    setIsUploadingState(next);
  }, []);

  const conversationId = useCallback((): string => {
    if (!conversationIdRef.current) {
      conversationIdRef.current = adapterRef.current.newConversationId();
    }
    return conversationIdRef.current;
  }, []);

  const stage = useCallback(
    (files: File[]) => {
      // An upload settles by clearing the list it took or putting back the files that failed, so a file
      // picked in the meantime would be wiped with it. Like a removal, a pick waits, and the user is told.
      if (uploadingRef.current) {
        notifyRef.current('Files are uploading. Add more once the upload has finished.', 'warning');
        return;
      }
      const accepted: File[] = [];
      for (const file of files) {
        const check = adapterRef.current.check(file);
        if (!check.ok) {
          notifyRef.current(check.reason, 'warning');
          continue;
        }
        accepted.push(file);
      }
      // A turn carries only so many files, counting those already staged and those uploaded for a turn
      // the server has not taken yet. What does not fit is left out, and the user is told which, so they
      // know what to remove.
      const limit = adapterRef.current.maxFiles;
      const room =
        limit === undefined
          ? accepted.length
          : Math.max(0, limit - stagedRef.current.length - pendingRef.current.length);
      const fitting = accepted.slice(0, room);
      const skipped = accepted.slice(room);
      if (limit !== undefined && skipped.length > 0) {
        const details = {
          limit: limit,
          added: fitting.length,
          skipped: skipped.map((skippedFile) => skippedFile.name),
        };
        const describe = adapterRef.current.tooManyFilesMessage;
        notifyRef.current(
          describe
            ? describe(details)
            : `A message can carry up to ${limit} file${limit === 1 ? '' : 's'}. Remove a file to add another.`,
          'warning',
        );
      }
      // And only so many bytes, for tools whose destination takes only so much in one request. Files are
      // taken in order while they fit; one that would push the total over is left out and named.
      const byteLimit = adapterRef.current.maxTotalBytes;
      let kept = fitting;
      if (byteLimit !== undefined && fitting.length > 0) {
        const bytesOf = adapterRef.current.bytesOf || ((file: File) => file.size);
        const stagedBytes = stagedRef.current.reduce((sum, staged) => sum + bytesOf(staged), 0);
        let running = stagedBytes;
        const fits: File[] = [];
        const tooBig: File[] = [];
        fitting.forEach((file) => {
          if (running + bytesOf(file) <= byteLimit) {
            fits.push(file);
            running += bytesOf(file);
          } else {
            tooBig.push(file);
          }
        });
        if (tooBig.length > 0) {
          const details = {
            limitBytes: byteLimit,
            totalBytes: stagedBytes + fitting.reduce((sum, file) => sum + bytesOf(file), 0),
            added: fits.length,
            skipped: tooBig.map((file) => file.name),
          };
          const describeTotal = adapterRef.current.tooBigTogetherMessage;
          notifyRef.current(
            describeTotal
              ? describeTotal(details)
              : 'Those files are more than one message can carry. Remove a file or attach smaller copies.',
            'warning',
          );
        }
        kept = fits;
      }
      if (kept.length > 0) {
        setStaged([...stagedRef.current, ...kept]);
      }
    },
    [setStaged],
  );

  const remove = useCallback(
    (index: number) => {
      // Removing a file mid-upload would not stop its upload, so it waits.
      if (uploadingRef.current) return;
      setStaged(stagedRef.current.filter((_, i) => i !== index));
    },
    [setStaged],
  );

  const upload = useCallback(
    async (groupId: string | undefined): Promise<UploadOutcome<TRef>> => {
      const files = stagedRef.current;
      if (files.length === 0) {
        return { status: 'ready', attachments: pendingRef.current };
      }
      // The upload is made for a team and checked against it, so there has to be one.
      if (!groupId) {
        notifyRef.current('Choose a team before attaching files.', 'error');
        return { status: 'no-team' };
      }

      const startGeneration = generationRef.current;
      setUploading(true);
      const settled = await Promise.allSettled(
        files.map((file) =>
          adapterRef.current.upload(file, {
            conversationId: conversationId(),
            groupId: groupId,
          }),
        ),
      );
      if (generationRef.current !== startGeneration) {
        // `reset` has already cleared the flag, and an upload that started after it may be running now:
        // this one is not the one to say the controls are free.
        return { status: 'cancelled' };
      }

      const succeeded: TRef[] = [];
      const failedFiles: File[] = [];
      const failureReasons: string[] = [];
      settled.forEach((outcome, index) => {
        if (outcome.status === 'fulfilled') {
          succeeded.push(outcome.value);
        } else {
          failedFiles.push(files[index]);
          // Kept in step with failedFiles, empty when the server gave none.
          failureReasons.push(
            outcome.reason instanceof Error
              ? outcome.reason.message
              : String(outcome.reason ?? ''),
          );
        }
      });
      setPending([...pendingRef.current, ...succeeded]);
      setUploading(false);

      if (failedFiles.length > 0) {
        setStaged(failedFiles);
        // One message per file, each with the server's own reason: it is the actionable half (an
        // expired session, not on the team, a limit and what to change, a file it could not open), it is
        // what a bug report needs, and several files can fail for different reasons.
        failedFiles.forEach((failed, index) => {
          const reason = failureReasons[index];
          notifyRef.current(
            reason
              ? `${failed.name}: ${reason}`
              : `${failed.name} failed to upload. Please try again.`,
            'error',
          );
          if (reason) console.error('[attachments] upload:', reason);
        });
        return { status: 'failed' };
      }

      setStaged([]);
      return { status: 'ready', attachments: pendingRef.current };
    },
    [conversationId, setPending, setStaged, setUploading],
  );

  const hasPendingUploads = useCallback(() => pendingRef.current.length > 0, []);

  const removePending = useCallback(
    (ref: TRef) => {
      setPending(pendingRef.current.filter((pending) => pending !== ref));
    },
    [setPending],
  );

  // The server has taken the turn, files included.
  const markSent = useCallback(() => {
    setPending([]);
  }, [setPending]);

  // A fresh conversation starts with no files: staged ones, ones already uploaded for the old
  // conversation, and the id they were filed under.
  const reset = useCallback(() => {
    generationRef.current += 1;
    setPending([]);
    conversationIdRef.current = null;
    setStaged([]);
    setUploading(false);
  }, [setPending, setStaged, setUploading]);

  return {
    accept: adapter.accept,
    stagedFiles: stagedFiles,
    isUploading: isUploading,
    stage: stage,
    remove: remove,
    upload: upload,
    hasPendingUploads: hasPendingUploads,
    pendingAttachments: pendingAttachments,
    removePending: removePending,
    markSent: markSent,
    reset: reset,
  };
};
